<template>
  <div class="tpl-editor">
    <!-- Top bar -->
    <header class="tpl-topbar">
      <a
        v-if="exitUrl"
        :href="exitUrl"
        class="tpl-topbar__exit"
        title="Back to campaign"
        aria-label="Exit template editor"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="19" y1="12" x2="5" y2="12"/>
          <polyline points="12 19 5 12 12 5"/>
        </svg>
      </a>

      <div class="tpl-topbar__left">
        <div class="tpl-topbar__eyebrow">
          <span class="tpl-topbar__chip">
            <span class="tpl-topbar__chip-dot" aria-hidden="true"></span>
            Super-admin
          </span>
          <span class="tpl-topbar__crumb">Template content</span>
        </div>
        <h1 class="tpl-topbar__title">{{ campaignName }}</h1>
      </div>

      <div class="tpl-topbar__actions">
        <span
          v-if="dirty"
          class="tpl-status tpl-status--dirty"
          role="status"
          aria-label="Unsaved changes"
        >
          <span class="tpl-status__dot" aria-hidden="true"></span>
          Unsaved
        </span>
        <span
          v-else
          class="tpl-status tpl-status--saved"
          role="status"
          aria-label="All changes saved"
        >
          <span class="tpl-status__dot" aria-hidden="true"></span>
          Saved
        </span>

        <button
          v-if="dirty"
          type="button"
          class="tpl-btn tpl-btn--ghost"
          :disabled="saving"
          @click="discard"
        >Discard</button>

        <a
          :href="publicUrl"
          target="_blank"
          rel="noopener"
          class="tpl-btn tpl-btn--ghost"
          title="Open public page in new tab"
        >
          Open public page
          <span aria-hidden="true" class="tpl-btn__arrow">↗</span>
        </a>

        <button
          type="button"
          class="tpl-btn tpl-btn--primary"
          :disabled="saving || !dirty"
          @click="save"
        >
          <span v-if="saving" class="tpl-btn__inner">
            <span class="tpl-spin" aria-hidden="true"></span>
            Saving
          </span>
          <span v-else-if="dirty" class="tpl-btn__inner">
            Save changes
            <kbd class="tpl-kbd">⌘S</kbd>
          </span>
          <span v-else class="tpl-btn__inner">Saved</span>
        </button>
      </div>
    </header>

    <!-- Main 3-column layout: rail | form | preview -->
    <div class="tpl-body">
      <!-- Section rail -->
      <nav
        class="tpl-rail"
        aria-label="Template sections"
      >
        <button
          v-for="s in sections"
          :key="s.key"
          type="button"
          :class="[
            'tpl-rail__item',
            { 'tpl-rail__item--active': s.key === active },
          ]"
          :aria-current="s.key === active ? 'page' : null"
          @click="active = s.key"
        >
          <span class="tpl-rail__icon" aria-hidden="true" v-html="s.icon"></span>
          <span class="tpl-rail__label">{{ s.label }}</span>
        </button>

        <div class="tpl-rail__spacer" aria-hidden="true"></div>

        <button
          type="button"
          class="tpl-rail__toggle"
          :title="formCollapsed ? 'Show form panel' : 'Hide form panel'"
          :aria-label="formCollapsed ? 'Show form panel' : 'Hide form panel'"
          :aria-pressed="!formCollapsed"
          @click="formCollapsed = !formCollapsed"
        >
          <span v-if="!formCollapsed" aria-hidden="true">‹</span>
          <span v-else aria-hidden="true">›</span>
        </button>
      </nav>

      <!-- Form panel -->
      <section
        class="tpl-form"
        :class="{ 'tpl-form--collapsed': formCollapsed }"
        :aria-hidden="formCollapsed"
      >
        <div class="tpl-form__scroll">
          <div class="tpl-form__inner">
            <HeroSectionEditor
              v-if="active === 'hero'"
              :content="content.hero"
              :upload-url="uploadUrl"
            />
            <GallerySectionEditor
              v-else-if="active === 'gallery'"
              :gallery="content.gallery"
              :upload-url="uploadUrl"
              @update:gallery="content.gallery = $event"
            />
            <FounderCtaSectionEditor
              v-else-if="active === 'founder_cta'"
              :content="content.founder_cta"
            />
            <ResearchModelSectionEditor
              v-else-if="active === 'research_model'"
              :content="content.research_model"
            />
            <PrecisionAnalysisSectionEditor
              v-else-if="active === 'precision_analysis'"
              :content="content.precision_analysis"
            />
            <SupportOnboardingSectionEditor
              v-else-if="active === 'support_onboarding'"
              :content="content.support_onboarding"
              :upload-url="uploadUrl"
            />
            <ActivitySectionEditor
              v-else-if="active === 'activity_section'"
              :content="content.activity_section"
            />
            <DonationWidgetSectionEditor
              v-else-if="active === 'donation_widget'"
              :content="content.donation_widget"
            />
            <SupportModalSectionEditor
              v-else-if="active === 'support_modal'"
              :content="content.support_modal"
              :upload-url="uploadUrl"
              @preview-step-change="previewStep = $event"
            />
            <ProgramsSectionEditor
              v-else-if="active === 'programs'"
              :programs="content.programs"
              @update:programs="content.programs = $event"
            />
            <FooterSectionEditor
              v-else-if="active === 'footer'"
              :content="content.footer"
              :upload-url="uploadUrl"
            />
          </div>
        </div>
      </section>

      <!-- Preview canvas (dominant) -->
      <TemplatePreviewPane
        class="tpl-preview"
        :src="previewIframeUrl"
        :active-section="active"
        :content="content"
        :preview-step="previewStep"
      />
    </div>

    <notifications position="top center" />
  </div>
</template>

<script>
import HeroSectionEditor from './template-editor/sections/HeroSectionEditor.vue';
import GallerySectionEditor from './template-editor/sections/GallerySectionEditor.vue';
import FounderCtaSectionEditor from './template-editor/sections/FounderCtaSectionEditor.vue';
import ResearchModelSectionEditor from './template-editor/sections/ResearchModelSectionEditor.vue';
import PrecisionAnalysisSectionEditor from './template-editor/sections/PrecisionAnalysisSectionEditor.vue';
import SupportOnboardingSectionEditor from './template-editor/sections/SupportOnboardingSectionEditor.vue';
import ActivitySectionEditor from './template-editor/sections/ActivitySectionEditor.vue';
import DonationWidgetSectionEditor from './template-editor/sections/DonationWidgetSectionEditor.vue';
import SupportModalSectionEditor from './template-editor/sections/SupportModalSectionEditor.vue';
import ProgramsSectionEditor from './template-editor/sections/ProgramsSectionEditor.vue';
import FooterSectionEditor from './template-editor/sections/FooterSectionEditor.vue';
import TemplatePreviewPane from './template-editor/TemplatePreviewPane.vue';

// Inline SVGs for the section rail — small glyphs, uniform stroke weight.
// Each 18x18 to fit the rail's compact icon slot.
const I = (d) => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;

export default {
  name: 'FoundationTemplateContentEditor',

  components: {
    HeroSectionEditor,
    GallerySectionEditor,
    FounderCtaSectionEditor,
    ResearchModelSectionEditor,
    PrecisionAnalysisSectionEditor,
    SupportOnboardingSectionEditor,
    ActivitySectionEditor,
    DonationWidgetSectionEditor,
    SupportModalSectionEditor,
    ProgramsSectionEditor,
    FooterSectionEditor,
    TemplatePreviewPane,
  },

  props: {
    campaignId: { type: [Number, String], required: true },
    campaignName: { type: String, required: true },
    initialContent: { type: Object, required: true },
    updateUrl: { type: String, required: true },
    uploadUrl: { type: String, required: true },
    previewIframeUrl: { type: String, required: true },
    publicUrl: { type: String, required: true },
    exitUrl: { type: String, default: '' },
  },

  data() {
    const content = this.cloneContent(this.initialContent);
    // Foundations whose stored template content predates the gallery editor
    // may lack a `gallery` key; guarantee an array so the section renders.
    if (!Array.isArray(content.gallery)) content.gallery = [];
    return {
      active: 'hero',
      saving: false,
      dirty: false,
      // The Quill rich-text editors emit an initial content-change event on
      // mount as they normalize `<p><br></p>` → `''`. Without this guard the
      // dirty flag would be set before the user ever interacts with the form.
      hydrated: false,
      content,
      formCollapsed: false,
      // Per-section preview-step (currently driven only by the
      // SupportModalSectionEditor's step tabs). null = use whatever the
      // modal naturally renders (Step 1).
      previewStep: null,
      sections: [
        { key: 'hero',               label: 'Hero',         icon: I('<path d="M3 4h18v14H3z"/><path d="M3 10h18"/><circle cx="8" cy="14" r="1.2"/>') },
        { key: 'gallery',            label: 'Gallery',      icon: I('<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>') },
        { key: 'founder_cta',        label: 'Founder CTA',  icon: I('<rect x="4" y="8" width="16" height="8" rx="2"/><path d="M8 12h8"/>') },
        { key: 'research_model',     label: 'Research',     icon: I('<path d="M6 3v5l-2 4v7h16v-7l-2-4V3"/><path d="M6 3h12"/>') },
        { key: 'precision_analysis', label: 'Precision',    icon: I('<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/>') },
        { key: 'support_onboarding', label: 'Support',      icon: I('<path d="M12 3l9 4.5v5c0 5-3.5 8-9 9-5.5-1-9-4-9-9v-5L12 3z"/>') },
        { key: 'activity_section',   label: 'Activity',     icon: I('<path d="M3 12h3l3-8 4 16 3-8h5"/>') },
        { key: 'donation_widget',    label: 'Donate Copy',   icon: I('<path d="M12 21s-7-4.5-7-10a4 4 0 017-2.65A4 4 0 0119 11c0 5.5-7 10-7 10z"/><path d="M9 12h6M12 9v6"/>') },
        { key: 'support_modal',      label: 'Support Modal', icon: I('<rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="11" r="1.6"/><path d="M3 16l5-3 4 3 4-2 5 3"/>') },
        { key: 'programs',           label: 'Programs',     icon: I('<path d="M4 7l8-4 8 4-8 4-8-4z"/><path d="M4 12l8 4 8-4"/><path d="M4 17l8 4 8-4"/>') },
        { key: 'footer',             label: 'Footer',       icon: I('<rect x="3" y="4" width="18" height="14" rx="2"/><path d="M3 14h18"/>') },
      ],
    };
  },

  watch: {
    content: {
      deep: true,
      handler() {
        if (this.hydrated) this.dirty = true;
      },
    },
    active() {
      // Each section owns its own previewStep (or no step at all).
      // Clearing on switch avoids carrying a stale step into a section
      // that doesn't use one.
      this.previewStep = null;
    },
  },

  mounted() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    // Two frames for Quill to finish its normalization emit-loop before we
    // start trusting content changes as user intent.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.hydrated = true;
      });
    });
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },

  methods: {
    cloneContent(source) {
      return JSON.parse(JSON.stringify(source || {}));
    },

    discard() {
      if (!this.dirty) return;
      if (!window.confirm('Discard all unsaved changes and revert to the last saved version?')) return;
      this.content = this.cloneContent(this.initialContent);
      this.$nextTick(() => { this.dirty = false; });
    },

    save() {
      if (this.saving || !this.dirty) return;
      this.saving = true;
      axios.put(this.updateUrl, this.content)
        .then((resp) => {
          if (resp.data && resp.data.content) {
            this.content = this.cloneContent(resp.data.content);
          }
          this.$nextTick(() => { this.dirty = false; });
          this.$notify({ type: 'success', text: resp.data.message || 'Saved.' });
        })
        .catch((err) => {
          const msg = err.response?.data?.message
            || (err.response?.data?.errors
              ? Object.values(err.response.data.errors).flat().join(' ')
              : null)
            || 'Save failed. Please review your inputs.';
          this.$notify({ type: 'error', text: msg });
        })
        .finally(() => {
          this.saving = false;
        });
    },

    onKeyDown(e) {
      const isSave = (e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S');
      if (isSave) {
        e.preventDefault();
        this.save();
      }
    },

    onBeforeUnload(e) {
      if (this.dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
  },
};
</script>

<style scoped>
/* Design tokens — scoped to the editor. */
.tpl-editor {
  --ink:       #0f172a;
  --ink-soft:  #1e293b;
  --muted:     #64748b;
  --muted-2:   #94a3b8;
  --line:      #e2e8f0;
  --line-soft: #f1f5f9;
  --bg:        #f8fafc;
  --surface:   #ffffff;
  --accent:    #db2777;
  --accent-soft: #fce7f3;
  --emerald:   #059669;
  --amber:     #d97706;

  /* Full-viewport immersive editor — escapes the app shell's sidebar cap so
     the preview canvas gets the whole browser width. The exit button in the
     top bar provides the way back. */
  position: fixed;
  inset: 0;
  z-index: 50;

  display: grid;
  grid-template-rows: auto 1fr;
  background: var(--bg);
  color: var(--ink);
  overflow: hidden;
  font-feature-settings: "ss01", "ss02";
}

/* ─────────────── Top bar ─────────────── */
.tpl-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 0.7rem 1.25rem 0.7rem 0.85rem;
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  position: relative;
  z-index: 20;
}
.tpl-topbar__exit {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  background: var(--line-soft);
  border: 1px solid transparent;
  text-decoration: none;
  transition: background 0.12s, color 0.12s, border-color 0.12s, transform 0.08s;
  flex-shrink: 0;
}
.tpl-topbar__exit:hover {
  background: var(--line);
  color: var(--ink);
  border-color: var(--line);
}
.tpl-topbar__exit:active {
  transform: translateX(-1px);
}
.tpl-topbar__left {
  min-width: 0;
  flex: 1;
}
.tpl-topbar__eyebrow {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.1rem;
}
.tpl-topbar__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.18rem 0.55rem 0.18rem 0.45rem;
  background: var(--ink);
  color: #fff;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.tpl-topbar__chip-dot {
  width: 5px; height: 5px;
  border-radius: 999px;
  background: var(--accent);
  box-shadow: 0 0 0 2px rgba(219, 39, 119, 0.22);
}
.tpl-topbar__crumb {
  color: var(--muted-2);
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
}
.tpl-topbar__title {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 650;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tpl-topbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.tpl-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}
.tpl-status__dot {
  width: 6px; height: 6px; border-radius: 999px;
}
.tpl-status--dirty {
  background: #fef3c7;
  color: #92400e;
}
.tpl-status--dirty .tpl-status__dot { background: var(--amber); box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.22); }
.tpl-status--saved {
  background: #d1fae5;
  color: #065f46;
}
.tpl-status--saved .tpl-status__dot { background: var(--emerald); }

/* ─────────────── Buttons ─────────────── */
.tpl-btn {
  appearance: none;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0.5rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.08s, background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  line-height: 1;
}
.tpl-btn__inner {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.tpl-btn__arrow {
  font-size: 0.78rem;
  opacity: 0.7;
}
.tpl-btn:active:not(:disabled) { transform: translateY(1px); }
.tpl-btn:disabled { cursor: not-allowed; }

.tpl-btn--primary {
  background: var(--ink);
  color: #fff;
  border-color: var(--ink);
}
.tpl-btn--primary:hover:not(:disabled) {
  background: #020617;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.15);
}
.tpl-btn--primary:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  border-color: #e2e8f0;
}
.tpl-btn--ghost {
  background: transparent;
  color: var(--ink);
  border-color: var(--line);
}
.tpl-btn--ghost:hover:not(:disabled) {
  background: var(--line-soft);
  border-color: #cbd5e1;
}

.tpl-spin {
  width: 10px; height: 10px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  animation: tpl-spin 0.6s linear infinite;
}
@keyframes tpl-spin { to { transform: rotate(360deg); } }

.tpl-kbd {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.35rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 0.65rem;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0;
}

/* ─────────────── 3-column body ─────────────── */
.tpl-body {
  display: grid;
  /* rail | form (collapsible) | preview (fills remainder) */
  grid-template-columns: 92px 420px minmax(0, 1fr);
  min-height: 0;
  position: relative;
}
.tpl-form--collapsed {
  display: none;
}
.tpl-body:has(.tpl-form--collapsed) {
  grid-template-columns: 92px minmax(0, 1fr);
}

/* ─────────────── Section rail ─────────────── */
.tpl-rail {
  background: var(--surface);
  border-right: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  padding: 0.6rem 0.4rem 0.4rem;
  gap: 0.15rem;
  overflow-y: auto;
}
.tpl-rail__item {
  appearance: none;
  background: transparent;
  border: none;
  padding: 0.55rem 0.3rem 0.6rem;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
  color: var(--muted);
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.005em;
  transition: background 0.12s, color 0.12s;
  position: relative;
}
.tpl-rail__item:hover {
  background: var(--line-soft);
  color: var(--ink);
}
.tpl-rail__item--active {
  background: var(--line-soft);
  color: var(--ink);
}
.tpl-rail__item--active::before {
  content: "";
  position: absolute;
  left: -0.4rem;
  top: 10%;
  bottom: 10%;
  width: 3px;
  border-radius: 0 4px 4px 0;
  background: var(--accent);
}
.tpl-rail__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
}
.tpl-rail__icon svg {
  display: block;
}
.tpl-rail__label {
  font-size: 0.68rem;
  line-height: 1.1;
  text-align: center;
}
.tpl-rail__spacer { flex-grow: 1; }
.tpl-rail__toggle {
  appearance: none;
  background: transparent;
  border: 1px solid var(--line);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  margin: 0.2rem auto 0;
  cursor: pointer;
  color: var(--muted);
  font-size: 1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s;
}
.tpl-rail__toggle:hover {
  background: var(--line-soft);
  color: var(--ink);
}

/* ─────────────── Form panel ─────────────── */
.tpl-form {
  background: var(--surface);
  border-right: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
.tpl-form__scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
}
.tpl-form__inner {
  padding: 1.5rem 1.5rem 3rem;
}

/* ─────────────── Preview ─────────────── */
.tpl-preview {
  min-width: 0;
  min-height: 0;
}

/* ─────────────── Responsive fallback ─────────────── */
@media (max-width: 960px) {
  .tpl-editor { height: auto; min-height: 100vh; }
  .tpl-body {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto 60vh;
  }
  .tpl-body:has(.tpl-form--collapsed) {
    grid-template-columns: 1fr;
  }
  .tpl-rail {
    flex-direction: row;
    border-right: none;
    border-bottom: 1px solid var(--line);
    overflow-x: auto;
    overflow-y: hidden;
  }
  .tpl-rail__spacer, .tpl-rail__toggle { display: none; }
  .tpl-rail__item--active::before {
    left: 10%;
    right: 10%;
    top: auto;
    bottom: 0;
    height: 3px;
    width: auto;
    border-radius: 4px 4px 0 0;
  }
  .tpl-form { border-right: none; border-bottom: 1px solid var(--line); }
}
</style>
